import React, { useState, useEffect } from "react";
import Select from "react-select";
import { X } from "lucide-react";
import { fetchRoles, fetchBranches, addUser, updateUser } from "../../../common/api/userService";

function AddEditUserModal({ user, onClose, onSave }) {
  const [formData, setFormData] = useState(
    user || {
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      role_id: null,
      branch_id: null,
    }
  );

  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [rolesData, branchesData] = await Promise.all([fetchRoles(), fetchBranches()]);
        setRoles(rolesData.map((role) => ({ value: role.role_id, label: role.name })));
        setBranches(branchesData.map((branch) => ({ value: branch.branch_id, label: branch.name })));
      } catch (error) {
        console.error("Failed to load options:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  // ✅ Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ Handle select dropdown changes
  const handleSelectChange = (selectedOption, field) => {
    setFormData({ ...formData, [field]: selectedOption ? selectedOption.value : null });
  };

  // ✅ Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage("");

    if (!formData.username || !formData.email || !formData.role_id) {
      setErrorMessage("Username, Email, and Role are required.");
      setSubmitting(false);
      return;
    }

    try {
      let result;
      if (user) {
        result = await updateUser(user.user_id, formData);
        setSuccessMessage(`User "${result.username}" updated successfully!`);
      } else {
        result = await addUser(formData);
        setSuccessMessage(`User "${result.username}" added successfully!`);
      }

      onSave(result);

      // Auto-close after success
      setTimeout(() => {
        onClose();
        setSuccessMessage("");
      }, 2000);
    } catch (error) {
      console.error("❌ API Error saving user:", error);
      setErrorMessage("Failed to save user. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg flex flex-col max-h-[90vh] relative">
        {/* ✅ Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-red-600">
          <X size={24} />
        </button>

        {/* ✅ Header */}
        <div className="p-6 border-b font-semibold text-lg bg-gray-100 rounded-t-xl">
          {user ? "Edit User" : "Add User"}
        </div>

        {/* ✅ Success & Error Messages */}
        {successMessage && <p className="bg-green-100 text-green-700 p-3">{successMessage}</p>}
        {errorMessage && <p className="bg-red-100 text-red-700 p-3">{errorMessage}</p>}

        {/* ✅ Form */}
        <form id="user-form" onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-grow space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="border p-2 rounded-md w-full"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="border p-2 rounded-md w-full"
                required
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="border p-2 rounded-md w-full"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="border p-2 rounded-md w-full"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <Select
                options={roles}
                value={roles.find((role) => role.value === formData.role_id)}
                onChange={(selected) => handleSelectChange(selected, "role_id")}
                placeholder="Select Role"
                isClearable
              />
            </div>

            {/* Branch */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Branch</label>
              <Select
                options={branches}
                value={branches.find((branch) => branch.value === formData.branch_id)}
                onChange={(selected) => handleSelectChange(selected, "branch_id")}
                placeholder="Select Branch"
                isClearable
              />
            </div>
          </div>
        </form>

        {/* ✅ Footer */}
        <div className="p-4 border-t bg-gray-100 flex justify-end space-x-3 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md">
            Cancel
          </button>
          <button
            type="submit"
            form="user-form"
            className={`px-4 py-2 text-white bg-blue-600 rounded-md ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddEditUserModal;